import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const timeAgo = (iso) => {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  return `${Math.floor(d / 86400)}d`;
};

export const NotificationsBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const loadCount = useCallback(async () => {
    if (!user) return;
    try { const r = await api.get("/notifications/unread_count"); setUnread(r.data.count || 0); } catch {}
  }, [user]);

  const loadList = useCallback(async () => {
    try { const r = await api.get("/notifications"); setItems(r.data || []); } catch {}
  }, []);

  useEffect(() => {
    if (!user) return;
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, [user, loadCount]);

  const onOpenChange = async (o) => {
    setOpen(o);
    if (o) {
      await loadList();
      try { await api.post("/notifications/read", {}); setUnread(0); } catch {}
    }
  };

  const go = (n) => {
    setOpen(false);
    if (n?.data?.collection_id) navigate(`/collection/${n.data.collection_id}`);
  };

  if (!user) return null;

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notifications-bell" className="relative h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-secondary/60 text-muted-foreground hover:text-foreground transition-colors outline-none"
          onPointerDown={(e) => e.preventDefault()} onClick={() => onOpenChange(!open)}>
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span data-testid="notifications-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 bg-card border-border p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-border text-sm font-medium">Notifications</div>
        <div className="max-h-96 overflow-y-auto" data-testid="notifications-list">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground px-4 py-8 text-center">No notifications yet.</p>
          ) : items.map((n) => (
            <button key={n.id} data-testid={`notification-${n.id}`} onClick={() => go(n)}
              className={`w-full text-left px-4 py-3 border-b border-border/60 hover:bg-secondary/40 transition-colors flex gap-3 ${n.read ? "" : "bg-primary/5"}`}>
              <Sparkles className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{n.title}</div>
                <div className="text-xs text-muted-foreground">{n.body}</div>
                <div className="text-[10px] text-muted-foreground/60 mt-0.5">{timeAgo(n.created_at)} ago</div>
              </div>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
